import React, { useState } from "react"
import { Select, Table, Card, Input, DatePicker } from "antd"
import moment from "moment"

const { Option } = Select

const extractYear = dateStr => {
  if (!dateStr) return null

  const tryFormats = [
    {
      regex: /^\d{2}\/\d{2}\/\d{4}$/, // dd/mm/yyyy
      parser: str => {
        const [day, month, year] = str.split("/")
        return new Date(`${year}-${month}-01`)
      },
    },
    {
      regex: /^\d{4}-\d{2}-\d{2}$/, // yyyy-mm-dd
      parser: str => new Date(str),
    },
    // Add more formats as needed
  ]

  for (const { regex, parser } of tryFormats) {
    if (regex.test(dateStr)) {
      const date = parser(dateStr)
      if (!isNaN(date)) return date.getFullYear()
    }
  }

  const fallback = new Date(dateStr)
  return !isNaN(fallback) ? fallback.getFullYear() : null
}

const PortfolioSummary = ({ data }) => {
  // Filters for each section
  const [gainsMonth, setGainsMonth] = useState(new Date().getMonth() + 1)
  const [derivativesMonth, setDerivativesMonth] = useState(
    new Date().getMonth() + 1
  )

  const stockDetails = data?.longTermHoldings || []
  const capitalGains = data?.capitalGains || []
  const derivatives = data?.derivativePL || []
  const [longSearch, setLongSearch] = useState("")
  const [shortSearch, setShortSearch] = useState("")
  const [tradingSearch, setTradingSearch] = useState("")

  const capitalGainsYears = capitalGains
    .map(item => extractYear(item.Sell_Date))
    .filter(year => year !== null)

  const derivativesYears = derivatives
    .map(item => extractYear(item.Sell_Date))
    .filter(year => year !== null)

  const allYears = [...capitalGainsYears, ...derivativesYears]

  const uniqueYears = Array.from(new Set(allYears)).sort((a, b) => b - a)

  const [dcurrentYear, setdcurrentyear] = useState(new Date().getFullYear())
  const [scurrentYear, setscurrentyear] = useState(new Date().getFullYear())
  const [Longtype, setLongType] = useState("Holdings")

  const getMonthNumber = dateStr => {
    if (!dateStr) return null

    const tryFormats = [
      {
        regex: /^\d{2}\/\d{2}\/\d{4}$/, // dd/mm/yyyy
        parser: str => {
          const [day, month, year] = str.split("/").map(Number)
          return { month, year }
        },
      },
      {
        regex: /^\d{4}-\d{2}-\d{2}$/, // yyyy-mm-dd
        parser: str => {
          const [year, month] = str.split("-").map(Number)
          return { month, year }
        },
      },
    ]

    for (const { regex, parser } of tryFormats) {
      if (regex.test(dateStr)) {
        return parser(dateStr)
      }
    }

    // fallback
    const fallback = new Date(dateStr)
    return !isNaN(fallback)
      ? { month: fallback.getMonth() + 1, year: fallback.getFullYear() }
      : null
  }

  const getYear = dateStr => {
    if (!dateStr) return null

    const tryFormats = [
      {
        regex: /^\d{2}\/\d{2}\/\d{4}$/, // dd/mm/yyyy
        parser: str => {
          const [day, month, year] = str.split("/")
          return new Date(`${year}-${month}-01`)
        },
      },
      {
        regex: /^\d{4}-\d{2}-\d{2}$/, // yyyy-mm-dd
        parser: str => {
          const [year, month] = str.split("-")
          return new Date(`${year}-${month}-01`)
        },
      },
      // Add more known formats if needed
    ]

    for (const { regex, parser } of tryFormats) {
      if (regex.test(dateStr)) {
        const date = parser(dateStr)
        if (!isNaN(date)) return date.getFullYear()
      }
    }

    // Fallback: native Date parse
    const fallbackDate = new Date(dateStr)
    if (!isNaN(fallbackDate)) {
      return fallbackDate.getFullYear()
    }

    return null
  }

  // Filtered data
  const filteredCapitalGains = capitalGains.filter(item => {
    const parsed = getMonthNumber(item.Sell_Date)
    if (!parsed) return false

    return parsed.month === gainsMonth && parsed.year === scurrentYear
  })

  const filteredDerivatives = derivatives.filter(item => {
    const parsed = getMonthNumber(item.Sell_Date)
    if (!parsed) return false

    return parsed.month === derivativesMonth && parsed.year === dcurrentYear
  })

  const formatRupee = value =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    })
      .format(value)
      .replace("₹", "₹ ")

  const parseDate = dateStr => {
    if (!dateStr) return new Date(0)
    const [day, month, year] = dateStr.split("/")
    return new Date(`${year}-${month}-${day}`)
  }

  const assetsColumns = [
    {
      title: "S.No",
      key: "index",
      render: (text, record, index) => index + 1,
      width: 80, // optional
      align: "center",
    },
    {
      title: "Stock Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Buy Date",
      key: "PurchaseDate",
      dataIndex: "PurchaseDate",
      sorter: (a, b) => parseDate(a.PurchaseDate) - parseDate(b.PurchaseDate),
    },
    {
      title: "Quantity",
      key: "purchaseQty",
      dataIndex: "purchaseQty",
      sorter: (a, b) => parseDate(a.purchaseQty) - parseDate(b.purchaseQty),
    },
    {
      title: "Invested",
      dataIndex: "investedValue",
      key: "investedValue",
      render: (text, record) => formatRupee(record.investedValue),
      sorter: (a, b) =>
        parseFloat(a.investedValue || 0) - parseFloat(b.investedValue || 0),
    },
    {
      title: "Current",
      key: "currentValue",
      render: (text, record) => formatRupee(record.currentValue),
      sorter: (a, b) =>
        parseFloat(a.currentValue || 0) - parseFloat(b.currentValue || 0),
    },
  ]

  const LongTermColumns = [
    {
      title: "S.No",
      key: "index",
      render: (text, record, index) => index + 1,
      width: 80, // optional
      align: "center",
    },
    {
      title: "Stock Name",
      dataIndex: "Symbol",
      key: "Symbol",
      sorter: (a, b) => a.Symbol.localeCompare(b.Symbol),
    },
    {
      title: "Purchase Quantity",
      key: "Purchase_Qty",
      dataIndex: "Purchase_Qty",
      sorter: (a, b) => parseDate(a.Purchase_Qty) - parseDate(b.Purchase_Qty),
    },
    {
      title: "Buy Price",
      dataIndex: "Purchase_Amount",
      key: "Purchase_Amount",
      render: (text, record) => formatRupee(record.Purchase_Amount),
      sorter: (a, b) =>
        parseFloat(a.Purchase_Amount || 0) - parseFloat(b.Purchase_Amount || 0),
    },
    {
      title: "Buy Date",
      dataIndex: "Purchase_Date",
      key: "Purchase_Date",
      sorter: (a, b) => parseDate(a.Purchase_Date) - parseDate(b.Purchase_Date),
    },
    {
      title: "Sell Quantity",
      key: "Sell_Qty",
      dataIndex: "Sell_Qty",
      sorter: (a, b) => parseDate(a.Sell_Qty) - parseDate(b.Sell_Qty),
    },
    {
      title: "Sell Price",
      dataIndex: "Sell_Amount",
      key: "Sell_Amount",
      render: (text, record) => formatRupee(record.Sell_Amount),
      sorter: (a, b) =>
        parseFloat(a.Sell_Amount || 0) - parseFloat(b.Sell_Amount || 0),
    },
    {
      title: "Sell Date",
      dataIndex: "Sell_Date",
      key: "Sell_Date",
      sorter: (a, b) => parseDate(a.Sell_Date) - parseDate(b.Sell_Date),
    },
    {
      title: "Booked P/L",
      dataIndex: "Book_PL",
      key: "Book_PL",
      render: (text, record) => (
        <span style={{ color: record.Book_PL < 0 ? "red" : "green" }}>
          {formatRupee(record.Book_PL)}
        </span>
      ),
      sorter: (a, b) => parseFloat(a.Book_PL || 0) - parseFloat(b.Book_PL || 0),
    },
  ]

  const capitalGainsColumns = [
    {
      title: "S.No",
      key: "index",
      render: (text, record, index) => index + 1,
      width: 80, // optional
      align: "center",
    },
    {
      title: "Stock Name",
      dataIndex: "Symbol",
      key: "Symbol",
      sorter: (a, b) => a.Symbol.localeCompare(b.Symbol),
    },
    {
      title: "Buy Price",
      dataIndex: "Purchase_Amount",
      key: "Purchase_Amount",
      render: (text, record) => formatRupee(record.Purchase_Amount),
      sorter: (a, b) =>
        parseFloat(a.Purchase_Amount || 0) - parseFloat(b.Purchase_Amount || 0),
    },
    {
      title: "Buy Date",
      dataIndex: "Purchase_Date",
      key: "Purchase_Date",
      sorter: (a, b) => parseDate(a.Purchase_Date) - parseDate(b.Purchase_Date),
    },
    {
      title: "Sell Price",
      dataIndex: "Sell_Amount",
      key: "Sell_Amount",
      render: (text, record) => formatRupee(record.Sell_Amount),
      sorter: (a, b) =>
        parseFloat(a.Sell_Amount || 0) - parseFloat(b.Sell_Amount || 0),
    },
    {
      title: "Sell Date",
      dataIndex: "Sell_Date",
      key: "Sell_Date",
      sorter: (a, b) => parseDate(a.Sell_Date) - parseDate(b.Sell_Date),
    },
    {
      title: "Booked P/L",
      dataIndex: "Book_PL",
      key: "Book_PL",
      render: (text, record) => (
        <span style={{ color: record.Book_PL < 0 ? "red" : "green" }}>
          {formatRupee(record.Book_PL)}
        </span>
      ),
      sorter: (a, b) => parseFloat(a.Book_PL || 0) - parseFloat(b.Book_PL || 0),
    },
  ]

  const derivativesColumns = [
    {
      title: "S.No",
      key: "index",
      render: (text, record, index) => index + 1,
      width: 80, // optional
      align: "center",
    },
    {
      title: "Index",
      dataIndex: "Scrip",
      key: "Scrip",
      sorter: (a, b) => a.Scrip.localeCompare(b.Scrip),
    },
    {
      title: "Buy Price",
      dataIndex: "Buy_Amount",
      key: "Buy_Amount",
      render: (text, record) => formatRupee(record.Buy_Amount),
      sorter: (a, b) =>
        parseFloat(a.Buy_Amount || 0) - parseFloat(b.Buy_Amount || 0),
    },
    {
      title: "Buy Date",
      dataIndex: "Buy_Date",
      key: "Buy_Date",
      sorter: (a, b) => parseDate(a.Buy_Date) - parseDate(b.Buy_Date),
    },
    {
      title: "Sell Price",
      dataIndex: "Sell_Amount",
      key: "Sell_Amount",
      render: (text, record) => formatRupee(record.Sell_Amount),
      sorter: (a, b) =>
        parseFloat(a.Sell_Amount || 0) - parseFloat(b.Sell_Amount || 0),
    },
    {
      title: "Sell Date",
      dataIndex: "Sell_Date",
      key: "Sell_Date",
      sorter: (a, b) => parseDate(a.Sell_Date) - parseDate(b.Sell_Date),
    },
    {
      title: "Booked P/L",
      dataIndex: "Book_PL",
      key: "Book_PL",
      render: (text, record) => (
        <span style={{ color: record.Book_PL < 0 ? "red" : "green" }}>
          {formatRupee(record.Book_PL)}
        </span>
      ),
      sorter: (a, b) => parseFloat(a.Book_PL || 0) - parseFloat(b.Book_PL || 0),
    },
  ]

  const [longDateRange, setLongDateRange] = useState(null)
  const [shortDateRange, setShortDateRange] = useState(null)
  const [tradingDateRange, setTradingDateRange] = useState(null)

  const filterBySellDate = (arr = [], daterange, type) => {
    if (!daterange || daterange.length !== 2) return arr

    const [start, end] = daterange
    const startDate = new Date(start)
    const endDate = new Date(end)
    endDate.setHours(23, 59, 59, 999) // include full end day

    return arr.filter(entry => {
      const sellDate =
        type === "long"
          ? Longtype === "Holdings"
            ? entry?.PurchaseDate
            : entry?.Purchase_Date
          : entry?.Sell_Date
      if (!sellDate) return false

      const sellDateObj = new Date(
        sellDate.split("/").reverse().join("-") // convert DD/MM/YYYY to YYYY-MM-DD
      )

      return sellDateObj >= startDate && sellDateObj <= endDate
    })
  }

  return (
    <div
      className="container-fluid py-4 px-0"
      style={{ backgroundColor: "#f2f2f2" }}
    >
      <div className="d-flex align-items-center justify-content-between mb-3 px-3">
        <h4 className="text-black">All Investments</h4>
      </div>

      <div className="row px-1">
        <div className="col-xl-12 mb-4 ">
          <Card
            className="Card-Data"
            title={
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
                <span>Long Term Investments</span>
                <div>
                  <DatePicker.RangePicker
                    size="small"
                    onChange={dates => setLongDateRange(dates)}
                    style={{ height: 30 }}
                    format="YYYY-MM-DD"
                    className="form-select1 rounded-2 py-2 text-dark me-2"
                  />
                  <Input
                    placeholder="Search..."
                    value={longSearch}
                    onChange={e => setLongSearch(e.target.value)}
                    size="small"
                    style={{ width: 200, height: 30 }}
                  />
                  <Select
                    value={Longtype}
                    onChange={setLongType}
                    size="small"
                    className="ms-2"
                    style={{ height: 30 }}
                  >
                    <Option key={"Holdings"} value={"Holdings"}>
                      {"Current Holdings"}
                    </Option>
                    <Option key={"Sold"} value={"Sold"}>
                      {"Sold Stocks"}
                    </Option>
                  </Select>
                </div>
              </div>
            }
          >
            <Table
              dataSource={filterBySellDate(
                (Longtype === "Holdings"
                  ? stockDetails
                  : capitalGains.filter(item => item?.Type === "LONGTERM")
                ).filter(item =>
                  JSON.stringify(item)
                    .toLowerCase()
                    .includes(longSearch.toLowerCase())
                ),
                longDateRange,
                "long"
              )}
              columns={
                Longtype === "Holdings" ? assetsColumns : LongTermColumns
              }
              pagination={false}
              size="small"
              scroll={{ y: 240 }}
              summary={pageData => {
                if (Longtype === "Holdings") {
                  // For current holdings, use investedValue and currentValue
                  const totalInvested = pageData.reduce(
                    (sum, item) => sum + parseFloat(item.investedValue || 0),
                    0
                  )
                  const totalCurrent = pageData.reduce(
                    (sum, item) => sum + parseFloat(item.currentValue || 0),
                    0
                  )
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell>
                          <b>Total ({pageData.length})</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell></Table.Summary.Cell>
                        <Table.Summary.Cell></Table.Summary.Cell>
                        <Table.Summary.Cell></Table.Summary.Cell>
                        <Table.Summary.Cell>
                          <b>{formatRupee(totalInvested)}</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell>
                          <b>{formatRupee(data?.portfolioCurrentValue)}</b>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  )
                } else if (Longtype === "Sold") {
                  const totalPL = pageData.reduce(
                    (sum, item) => sum + parseFloat(item.Book_PL || 0),
                    0
                  )
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell>
                          <b>Total ({pageData.length})</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell></Table.Summary.Cell>
                        <Table.Summary.Cell></Table.Summary.Cell>
                        <Table.Summary.Cell></Table.Summary.Cell>
                        <Table.Summary.Cell></Table.Summary.Cell>
                        <Table.Summary.Cell></Table.Summary.Cell>
                        <Table.Summary.Cell></Table.Summary.Cell>
                        <Table.Summary.Cell></Table.Summary.Cell>
                        <Table.Summary.Cell>
                          <b style={{ color: totalPL < 0 ? "red" : "green" }}>
                            {formatRupee(totalPL)}
                          </b>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  )
                }
                return null // fallback
              }}
            />
          </Card>
        </div>
        <div className="col-xl-12 mb-4">
          <Card
            className="Card-Data"
            title={
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
                <span>Short Term Investments</span>
                <div>
                  <DatePicker.RangePicker
                    size="small"
                    onChange={dates => setShortDateRange(dates)}
                    style={{ height: 30 }}
                    format="YYYY-MM-DD"
                    className="form-select1 rounded-2 py-2 text-dark me-2"
                  />
                  <Input
                    placeholder="Search..."
                    value={shortSearch}
                    onChange={e => setShortSearch(e.target.value)}
                    size="small"
                    className="me-2"
                    style={{ width: 200, height: 30 }}
                  />

                  <Select
                    value={gainsMonth}
                    onChange={setGainsMonth}
                    size="small"
                    style={{ width: 100, height: 30 }}
                  >
                    {[
                      "Jan",
                      "Feb",
                      "Mar",
                      "Apr",
                      "May",
                      "Jun",
                      "Jul",
                      "Aug",
                      "Sep",
                      "Oct",
                      "Nov",
                      "Dec",
                    ].map((month, idx) => (
                      <Option key={idx + 1} value={idx + 1}>
                        {month}
                      </Option>
                    ))}
                  </Select>
                  <Select
                    value={scurrentYear}
                    onChange={setscurrentyear}
                    size="small"
                    style={{ width: 100, height: 30 }}
                    className="ms-2"
                  >
                    {uniqueYears.map(month => (
                      <Option key={month} value={month}>
                        {month}
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
            }
          >
            <Table
              dataSource={filterBySellDate(
                shortDateRange
                  ? capitalGains
                  : filteredCapitalGains
                      ?.filter(item => item?.Type !== "LONGTERM") // First filter out LONGTERM
                      .filter(
                        item =>
                          JSON.stringify(item)
                            .toLowerCase()
                            .includes(tradingSearch.toLowerCase()) // Then apply search filter
                      ),
                shortDateRange,
                "short"
              ).map((item, i) => ({ ...item, key: i }))}
              columns={capitalGainsColumns}
              pagination={false}
              size="small"
              scroll={{ y: 240 }}
              summary={pageData => {
                const totalPL = pageData.reduce(
                  (sum, item) => sum + parseFloat(item.Book_PL),
                  0
                )
                const isLoss = totalPL < 0
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell>
                        <b>Total ({pageData.length})</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell></Table.Summary.Cell>
                      <Table.Summary.Cell></Table.Summary.Cell>
                      <Table.Summary.Cell></Table.Summary.Cell>
                      <Table.Summary.Cell></Table.Summary.Cell>
                      <Table.Summary.Cell></Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <div
                          style={{
                            color: isLoss ? "red" : "black",
                            fontWeight: "bold",
                          }}
                        >
                          {formatRupee(totalPL)}
                        </div>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )
              }}
            />
          </Card>
        </div>
        <div className="col-xl-12 ">
          <Card
            className="Card-Data"
            title={
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
                <span>Trading</span>
                <div>
                  <DatePicker.RangePicker
                    size="small"
                    onChange={dates => setTradingDateRange(dates)}
                    style={{ height: 30 }}
                    format="YYYY-MM-DD"
                    className="form-select1 rounded-2 py-2 text-dark me-2"
                  />
                  <Input
                    placeholder="Search..."
                    value={tradingSearch}
                    onChange={e => setTradingSearch(e.target.value)}
                    size="small"
                    className="me-2"
                    style={{ width: 200, height: 30 }}
                  />

                  <Select
                    value={derivativesMonth}
                    onChange={setDerivativesMonth}
                    size="small"
                    style={{ width: 100, height: 30 }}
                  >
                    {[
                      "Jan",
                      "Feb",
                      "Mar",
                      "Apr",
                      "May",
                      "Jun",
                      "Jul",
                      "Aug",
                      "Sep",
                      "Oct",
                      "Nov",
                      "Dec",
                    ].map((month, idx) => (
                      <Option key={idx + 1} value={idx + 1}>
                        {month}
                      </Option>
                    ))}
                  </Select>
                  <Select
                    value={dcurrentYear}
                    onChange={setdcurrentyear}
                    size="small"
                    style={{ width: 100, height: 30 }}
                    className="ms-2"
                  >
                    {uniqueYears.map(month => (
                      <Option key={month} value={month}>
                        {month}
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
            }
          >
            <Table
              dataSource={filterBySellDate(
                tradingDateRange
                  ? derivatives
                  : filteredDerivatives.filter(item =>
                      JSON.stringify(item)
                        .toLowerCase()
                        .includes(tradingSearch.toLowerCase())
                    ),
                tradingDateRange,
                "short"
              ).map((item, i) => ({ ...item, key: i }))}
              columns={derivativesColumns}
              pagination={false}
              size="small"
              scroll={{ y: 240 }}
              summary={pageData => {
                const totalPL = pageData.reduce(
                  (sum, item) => sum + parseFloat(item.Book_PL),
                  0
                )
                const isLoss = totalPL < 0
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell>
                        <b>Total ({pageData.length})</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell></Table.Summary.Cell>
                      <Table.Summary.Cell></Table.Summary.Cell>
                      <Table.Summary.Cell></Table.Summary.Cell>
                      <Table.Summary.Cell></Table.Summary.Cell>
                      <Table.Summary.Cell></Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <div
                          style={{
                            color: isLoss ? "red" : "black",
                            fontWeight: "bold",
                          }}
                        >
                          {formatRupee(totalPL)}
                        </div>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )
              }}
            />
          </Card>
        </div>
      </div>
    </div>
  )
}

export default PortfolioSummary
